import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { registerToastHandler, unregisterToastHandler } from '../utils/toastBridge';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    addToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_DURATION_MS = 3000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    // Contatore per id univoci: Date.now() può ripetersi su chiamate ravvicinate.
    const idCounter = useRef(0);
    // Tracciamento timer per cleanup esplicito (sia al click X sia all'unmount).
    const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const removeToast = useCallback((id: number) => {
        const handle = timers.current.get(id);
        if (handle) {
            clearTimeout(handle);
            timers.current.delete(id);
        }
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const addToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = ++idCounter.current;
        setToasts(prev => [...prev, { id, message, type }]);
        const handle = setTimeout(() => {
            timers.current.delete(id);
            setToasts(prev => prev.filter(t => t.id !== id));
        }, TOAST_DURATION_MS);
        timers.current.set(id, handle);
    }, []);

    // Registra il toast per l'interceptor axios e pulisce tutto all'unmount.
    useEffect(() => {
        registerToastHandler(addToast);
        return () => {
            unregisterToastHandler();
            timers.current.forEach(handle => clearTimeout(handle));
            timers.current.clear();
        };
    }, [addToast]);

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}
            <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border animate-in slide-in-from-right-10 fade-in duration-300 ${
                            toast.type === 'success' ? 'bg-white border-green-200 text-green-800' :
                                toast.type === 'error' ? 'bg-white border-red-200 text-red-800' :
                                    'bg-white border-blue-200 text-blue-800'
                        }`}
                    >
                        {toast.type === 'success' && <CheckCircle size={18} className="text-green-500" />}
                        {toast.type === 'error' && <AlertCircle size={18} className="text-red-500" />}
                        {toast.type === 'info' && <Info size={18} className="text-blue-500" />}
                        <span className="text-sm font-medium">{toast.message}</span>
                        <button type="button" onClick={() => removeToast(toast.id)} className="text-gray-400 hover:text-gray-600">
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) throw new Error('useToast must be used within a ToastProvider');
    return context;
};
